/* eslint-disable react-hooks/exhaustive-deps */
import { Bar } from '@antv/g2plot';
import { Typography } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const getText = {
  locations: 'Locations',
  stakeholders: 'Stakeholders',
  lirs: 'LIRs',
  events: 'Events',
  assessedSubjects: 'Assessed Subjects',
  identifiedTradeRelationship: 'Identified Trade Relationship',
  completedTasks: 'Completed Tasks',
};

const Text = Typography.Text;

export const CustomTooltipContent = (data, t) => {
  const items = data ? data.map ? data.map((d) => `
    <div class="item">
      <div>
        <div class="identifier" style="background: ${d.color}"></div>
      </div>
      <div class="title">
        <span>${t(d.name)}</span>
      </div>
      <div class="value">
        <span>${d.value}</span>
      </div>
    </div>
  `) : [] : [];

  return `
    <div class="custom-tooltip">
      ${items.join('')}
    </div>
  `;
};

export const StakeholdersDistributionTooltip = (data, t) => {
  const items = data ? data.map ? data.map((d) => `
    <div class="item">
      <div>
        <div class="identifier" style="background: ${d.color}"></div>
      </div>
      <div class="title">
        <span>${t(d.name)}</span>
      </div>
      <div class="value">
        <span>${d.data.realValue}</span>
      </div>
    </div>
  `) : [] : [];

  return `
    <div class="custom-tooltip">
      ${items.join('')}
    </div>
  `;
};

const getNameWithSpaces = (text = '') => {
  const length = text.length;
  if (length > 14) {
    if (text.includes(' ')) {
      const splitted = text.split(' ');
      let textToReturn = '';
      splitted.forEach((s, i) => {
        if (i === splitted.length - 1) {
          textToReturn += s;
        } else {
          if (s.length + splitted[i + 1].length < 14) {
            textToReturn += `${s} `;
          } else {
            textToReturn += `${s}\n`;
          }
        }
      });
      return textToReturn;
    } else {
      return `${text.slice(0, 13)}...`;
    }
  }
  return text;
};

export const useChartHelperColumns = ({
  t = (s) => s,
  metricsValues = [],
  data,
  type,
  isEmpty,
}) => {
  const container = useRef();
  const [graph, setGraph] = useState(null);
  const toggledMetricsValues = useMemo(() => metricsValues.filter((n) => n.toggled), [metricsValues]);

  const mappedData = useMemo(() => {
    let dataToMap = data;
    let otherData;
    if (data) {
      const d = [];

      if (data.length > 5) {
        dataToMap = data.slice(0, 5);
        otherData = data.slice(5).map((d) => {
          let items = {};
          Object.entries(d.items).forEach(([k, v]) => {
            items[k] = v;
          });
          return {
            items,
            label: d.label,
          };
        });
      }

      const mapData = (i) => Object.entries(i.items).forEach(([k, v], j) => {
        d.push({
          label: i.label,
          type: k,
          value: v,
          isFirst: j === 0,
        });
      });

      dataToMap.forEach((i) => mapData(i));
      if (otherData) {
        const other = {};
        otherData.forEach((d) => {
          Object.entries(d.items).forEach(([k, v]) => {
            other[k] = other[k] ? other[k] += v : v;
          });
        });

        Object.entries(other).forEach(([k, v], i) => {
          d.push({
            label: t('Other'),
            type: k,
            value: v,
            sources: otherData,
            isFirst: i === 0,
          });
        });
      }

      if (isEmpty) {
        const _new = [];

        [' ', '  ', '   ', '    ', '     ', '      '].forEach((l) => {
          _new.push({
            label: l,
            type: 'empty',
            value: 200,
            sources: otherData,
            isFirst: true,
          });
        })
        return _new;
      }

      return d;
    }

    return null;
  }, [data, isEmpty]);

  const color = useMemo(() => {
    const toggledTypes = toggledMetricsValues.map((n) => n.type);
    const filtered = (mappedData || []).filter((d) => toggledTypes.includes(d.type));
    const _total = filtered.reduce((all, val) => {
      return val.value + all;
    }, 0);
    if (isEmpty || !_total) {
      return ['#FAFAFA'];
    }

    return toggledMetricsValues.map((n) => n.color)
  }, [toggledMetricsValues, isEmpty, mappedData]);

  const filteredData = useMemo(() => {
    if (toggledMetricsValues && mappedData) {
      const toggledTypes = toggledMetricsValues.map((n) => n.type);
      const filtered = mappedData.filter((d) => toggledTypes.includes(d.type));
      const _total = filtered.reduce((all, val) => {
        return val.value + all;
      }, 0);
      if (!_total) {
        return mappedData
          .filter((d) => d.isFirst)
          .map((d) => ({ ...d, value: 200, isEmpty: true }));
      }

      return mappedData.filter((d) => toggledTypes.includes(d.type));
    }
    return null;
  }, [toggledMetricsValues, mappedData, isEmpty]);

  const tooltipTypes = {
    identification: {
      subjects: 120,
      identified: 80,
      container: 200,
    },
    tasks: {
      subjects: 160,
      identified: 80,
      container: 240,
    }
  };

  const typeConfig = tooltipTypes[type];

  const tooltip = {
    customContent: (a, data) => {
      if (isEmpty) {
        return '';
      }
      if (data[0] && data[0].data) {
        let isOther = false;

        if (data[0].data.isEmpty) {
          return '';
        }

        if (data[0].data.sources) {
          isOther = true;
        }

        const item = isOther ? data[0].data.sources.map((d) => ({
          subjects: d.label,
          identified: Object.entries(d.items).map(([k, v]) => ({
            value: v,
            label: t(getText[k]),
          })),
        })) : data.map((d) => ({
          identified: Number(d.value).toLocaleString('en-US'),
          subjects: t(getText[d.name]),
        }));

        const config = [
          {
            children: {
              subjects: {
                style: `width: ${typeConfig.subjects}px`,
                type: 'text',
                childStyle: `width: ${typeConfig.subjects - 10}px`,
              },
            },
          },
          {
            children: {
              identified: {
                style: isOther ? 'flex: 1;' : `width: ${typeConfig.identified}px;`,
                type: isOther ? 'textGroup' : 'text',
                childStyle: `width: ${typeConfig.identified - 10}px`,
              },
            },
          },
        ];
        const tooltipWithColumns = new TooltipWithColumns(item, config);

        return `
                <div style="
                  padding-top: 12px;
                  padding-bottom: 12px;
                  "
                >
                  <div style="margin-bottom: 12px; display: flex; flex-direction: row;">
                    <h2 style="margin-bottom: 0px; font-size: 14px; font-weight: 700; color: black">
                      ${a}
                    </h2>
                  </div>
                  <div style="display: flex; flex-direction: column;">
                    <div style="display: flex;">
                      <div style="
                        width: ${typeConfig.subjects}px;"
                      >
                        <label style="font-size: 10px; color: #2E90FA; font-weight: 700">${t(isOther ? t('Source') : t('Subjects'))}</label>
                      </div>
                      <div style="
                      ${isOther ? "flex: 1;" : `width: ${typeConfig.identified}px`};
                      ">
                        <label style="font-size: 10px; color: #2E90FA; font-weight: 700">${t(isOther ? t('Subjects Identified') : t('Identified'))}</label>
                      </div>
                    </div>
                    <div style="border-bottom: 1px solid #CCCCCC;"></div>
                    ${tooltipWithColumns.rows()}
                    </div>
                </div>
              `;
      }
      return '';
    },
  }

  useEffect(() => {
    if (mappedData) {
      if (!graph) {
        const stackedBarPlot = new Bar(container.current, {
          data: filteredData,
          isGroup: true,
          xField: 'value',
          yField: 'label',
          yAxis: {
            label: {
              formatter: (a) => getNameWithSpaces(a),
            },
          },
          xAxis: {
            label: {
              formatter: (a) => Number(a).toLocaleString("en-US"),
            }
          },
          seriesField: 'type',
          marginRatio: 0,
          label: false,
          legend: false,
          animation: false,
          color,
          tooltip,
        });

        stackedBarPlot.render();
        setGraph(stackedBarPlot);
      } else {
        graph.update({
          data: filteredData,
          animation: false,
          color,
          tooltip,
        });
      }
    } else if (graph) {
      graph.update({ data: [] });
    }
  }, [data, metricsValues]);

  return {
    container,
  }
}

export class TooltipWithColumns {
  constructor(data, config) {
    this.data = data;
    this.config = config;
  }

  types = {
    text: (content, style) => `
      <p style="color: black; font-size: 12px; font-weight: 500; margin-bottom: 0px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; padding: 0px; ${style}">
        ${content}
      </p>
    `,
    progress: (progress) => `
      <div style="display: flex">
        <div style="display: flex; flex-direction: column; justify-content: center;">
          <div style="width: 60px; height: 8px; background: #C4C4C4; border-radius: 10px">
            <div style="height: 100%; width: ${progress || 0}%; border-radius: 10px; background: #2E90FA"></div>
          </div>
        </div>
        <div>
          <p style="margin-left: 10px; margin-bottom: 0px; font-size: 12px; font-weight: 700; color: #595959;">${progress || 0}%</p>
        </div>
      </div>
    `,
    textGroup: (content) => {
      return `
        <div style="display: flex; gap: 10px;">
          ${content.map((c) => `
            <div style="display: flex;">
              <label style="font-weight: 700; color: black; margin-bottom: 0px; font-size: 10px;">${c.value}</label>
              <label style="margin-left: 4px; font-weight: 400; font-size: 10px; color: black; margin-bottom: 0px;">${c.label}</label>
            </div>
          `).join('')}
        </div>
      `;
    },
  };

  row = (type, style, data, childStyle) => `
    <div style="${style}">
      ${this.types[type](data, childStyle)}
    </div>
  `;

  getRows = (item) => {
    let rows = ``;
    this.config.forEach((c) => {
      Object.entries(c.children).forEach(([k, v]) => {
        rows += this.row(v.type, v.style, item[k], v.childStyle);
      });
    });
    return rows;
  };

  rows = () => this.data.map((item) => `
    <div style="display: flex; border-bottom: 1px solid #CCCCCC; padding-top: 6px; padding-bottom: 6px;">
      ${this.getRows(item)}
    </div>
  `).join('');
}

export const useChartHelperDaily = ({
  metricsValues = [],
  period = 'daily',
  data = null,
  frameActions = null,
  t = (t) => t,
  tooltTip = () => '',
  graph = null,
}) => {
  const [daily, setDaily] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [monthly, setMonthly] = useState(null);

  const getFilteredData = useCallback((dataToFilter) => {
    if (metricsValues && dataToFilter) {
      const toggledValues = metricsValues.filter((v) => v.toggled).map((v) => v.label);
      return dataToFilter.filter((d) => toggledValues.includes(d.series));
    }
    return [];
  }, [metricsValues]);

  const getFilteredColors = useMemo(() =>
    metricsValues ? metricsValues.filter((v) => v.toggled).map((v) => v.color) : [], [metricsValues]);

  const getActiveFilterData = useMemo(() => {
    switch (period) {
      case 'daily':
        return daily;
      case 'weekly':
        return weekly;
      case 'monthly':
        return monthly;
      default:
        return daily;
    }
  }, [period, monthly, daily, weekly]);

  useEffect(() => {
    if (!data && frameActions) {
      frameActions.loading(true);
    } else if (frameActions) {
      frameActions.loading(false);
    }
  }, [data, frameActions]);

  const initialConfig = useCallback((metricsValues) => ({
    xField: 'type',
    yField: 'value',
    seriesField: 'series',
    legend: false,
    xAxis: {
      label: {
        style: {
          fontSize: 10,
        },
      },
    },
    color: metricsValues ? metricsValues.map((v) => v.color) : [],
    tooltip: {
      customContent: (_, data) => tooltTip(data, t),
    },
  }), []);

  useEffect(() => {
    if (graph) {
      graph.update({
        tooltip: {
          customContent: (_, data) => CustomTooltipContent(data, t),
        },
      });
    }
  }, [t]);

  return {
    getFilteredData,
    getFilteredColors,
    getActiveFilterData,
    setDaily,
    setWeekly,
    setMonthly,
    initialConfig,
  };
};

export const useChartHelper = ({
  metricsValues = [],
  period = 'pastMonth',
  data = null,
  frameActions = null,
  t = (t) => t,
  tooltTip = () => '',
  graph = null,
}) => {
  const [daily, setDaily] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [monthly, setMonthly] = useState(null);

  const getFilteredData = useCallback((dataToFilter) => {
    if (metricsValues && dataToFilter) {
      const toggledValues = metricsValues.filter((v) => v.toggled).map((v) => v.label);
      return dataToFilter.filter((d) => toggledValues.includes(d.series));
    }
    return [];
  }, [metricsValues]);

  const getFilteredColors = useMemo(() =>
    metricsValues ? metricsValues.filter((v) => v.toggled).map((v) => v.color) : [], [metricsValues]);

  const getActiveFilterData = useMemo(() => {
    switch (period) {
      case 'pastMonth':
        return daily;
      case 'past3Months':
        return weekly;
      case 'past12Months':
        return monthly;
      default:
        return daily;
    }
  }, [period, monthly, daily, weekly]);

  useEffect(() => {
    if (!data && frameActions) {
      frameActions.loading(true);
    } else if (frameActions) {
      frameActions.loading(false);
    }
  }, [data, frameActions]);

  const initialConfig = useCallback((metricsValues) => ({
    xField: 'type',
    yField: 'value',
    seriesField: 'series',
    legend: false,
    xAxis: {
      label: {
        style: {
          fontSize: 10,
        },
      },
    },
    color: metricsValues ? metricsValues.map((v) => v.color) : [],
    tooltip: {
      customContent: (_, data) => tooltTip(data, t),
    },
  }), []);

  useEffect(() => {
    if (graph) {
      graph.update({
        tooltip: {
          customContent: (_, data) => CustomTooltipContent(data, t),
        },
      });
    }
  }, [t]);

  return {
    getFilteredData,
    getFilteredColors,
    getActiveFilterData,
    setDaily,
    setWeekly,
    setMonthly,
    initialConfig,
  };
};

export const usePopOverHelper = () => {
  const renderPopOver = useCallback((content, children, className = '', onClick = () => { }) => (
    <Text
      style={{ width: 'calc(100%)' }}
      className={className}
      ellipsis={{ tooltip: content }}
      onClick={onClick}
    >
      {children || content}
    </Text>
  ), []);

  return {
    renderPopOver,
  };
};
